// src/feature-module/student/StudentCVBuilder.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaDownload,
  FaPlus,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaUser,
  FaSearchPlus,
  FaSearchMinus,
  FaUndo,
  FaChevronDown,
  FaChevronUp,
  FaLock,
  FaSpinner,
  FaIdCard,
} from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";

// ─── Types ────────────────────────────────────────────────────────────────────

type MainSectionType =
  | "education"
  | "studium"
  | "weiterbildung"
  | "experience"
  | "praktikum"
  | "volunteering"
  | "languages"
  | "skills"
  | "activities";

interface EduEntry {
  id: string;
  dateRange: string;
  title: string;
  classLevel?: string;
  tag: string;
  institution: string;
  fields: { id: string; key: string; value: string; isGrade?: boolean }[];
  // work-entry structured fields
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
  isCurrentJob?: boolean;
  employmentType?: string;
  careerLevel?: string;
  activityField?: string;
  description?: string;
  industry?: string;
  orgType?: string;
  companySize?: string;
  location?: string;
  website?: string;
}

interface LangEntry {
  id: string;
  name: string;
  level: string;
}

interface SimpleEntry {
  id: string;
  value: string;
}

interface MainSection {
  id: string;
  type: MainSectionType;
  label: string;
  entries: EduEntry[] | LangEntry[] | SimpleEntry[];
}

interface PersonalInfo {
  firstName: string;
  lastName: string;
  subtitle: string;
  address: string;
  phone: string;
  email: string;
  birthDate: string;
  birthPlace: string;
  nationality: string;
  maritalStatus: string;
  hobbies: SimpleEntry[];
}

interface PhotoState {
  base64: string | null;
  x: number;
  y: number;
  zoom: number;
}

interface CVState {
  personal: PersonalInfo;
  photo: PhotoState;
  sections: MainSection[];
  format?: "classic" | "format3" | "format2";
  themeId?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TC = "#2C4A6B";
const LS_KEY = "cvBuilder_state";

interface CV2Theme { id: string; name: string; headerBg: string; accent: string; light?: boolean; }
const CV2_THEMES: CV2Theme[] = [
  { id: "navy-gold",       name: "Navy & Gold",       headerBg: "#1C2B4A", accent: "#C9A84C" },
  { id: "forest-amber",    name: "Forest & Amber",    headerBg: "#1A3A1A", accent: "#D4A017" },
  { id: "burgundy-silver", name: "Burgundy & Silver", headerBg: "#4A1A24", accent: "#B0B8C1" },
  { id: "charcoal-cyan",   name: "Charcoal & Cyan",   headerBg: "#2A2A2A", accent: "#009fe3" },
  { id: "purple-peach",    name: "Purple & Peach",    headerBg: "#2D1A4A", accent: "#FFAB76" },
  { id: "teal-coral",      name: "Teal & Coral",      headerBg: "#0D3333", accent: "#FF7F7F" },
  { id: "cream-navy",      name: "Cream & Navy",      headerBg: "#F0EBE0", accent: "#1C2B4A", light: true },
  { id: "sky-slate",       name: "Sky & Slate",       headerBg: "#E3EEF7", accent: "#2C4A6B", light: true },
  { id: "sage-forest",     name: "Sage & Forest",     headerBg: "#E8F0EA", accent: "#1A4A2A", light: true },
];

interface CV3Theme { id: string; name: string; sideBg: string; accent: string; light?: boolean; }
const CV3_THEMES: CV3Theme[] = [
  { id: "navy-cyan",  name: "Navy & Cyan",   sideBg: "#1C2B4A", accent: "#009fe3" },
  { id: "forest",     name: "Forest Green",  sideBg: "#1A3A1A", accent: "#4CAF50" },
  { id: "burgundy",   name: "Burgundy",      sideBg: "#4A1A24", accent: "#E57373" },
  { id: "light-blue", name: "Light Blue",    sideBg: "#E8F0F8", accent: "#2C4A6B", light: true },
  { id: "warm-sand",  name: "Warm Sand",     sideBg: "#F5EFE6", accent: "#8B5E3C", light: true },
  { id: "soft-sage",  name: "Soft Sage",     sideBg: "#EAF0EA", accent: "#2D6A2D", light: true },
];

const ALL_SECTION_DEFS: { type: MainSectionType; label: string }[] = [
  { type: "education", label: "Schulbildung" },
  { type: "studium", label: "Studium / Hochschulbildung" },
  { type: "weiterbildung", label: "Weiterbildung / Zertifikate" },
  { type: "experience", label: "Berufserfahrung" },
  { type: "praktikum", label: "Praktikum" },
  { type: "volunteering", label: "Ehrenamt / Freiwilligenarbeit" },
  { type: "languages", label: "Sprachkenntnisse" },
  { type: "skills", label: "Fähigkeiten" },
  { type: "activities", label: "Außerschulische Aktivitäten" },
];

const DEFAULT_STATE: CVState = {
  personal: {
    firstName: "",
    lastName: "",
    subtitle: "",
    address: "",
    phone: "",
    email: "",
    birthDate: "",
    birthPlace: "",
    nationality: "",
    maritalStatus: "Ledig",
    hobbies: [],
  },
  photo: { base64: null, x: 0, y: 0, zoom: 1 },
  format: "classic",
  themeId: "navy-cyan",
  sections: [
    { id: "sec-edu", type: "education", label: "Schulbildung", entries: [] },
    { id: "sec-lang", type: "languages", label: "Sprachkenntnisse", entries: [] },
    { id: "sec-skill", type: "skills", label: "Fähigkeiten", entries: [] },
  ],
};

// ─── Small helpers ────────────────────────────────────────────────────────────

const iCls = "w-full border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#4A729A] bg-white";
const iClsNoW = "border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#4A729A] bg-white";
const addBtnCls = "flex items-center gap-1.5 text-xs text-[#4A729A] border border-dashed border-[#4A729A] rounded px-2.5 py-1 mt-1 hover:bg-blue-50";

const FormBox: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
      <span className="text-sm font-bold text-gray-700">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const FL: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

// ─── Date helpers ─────────────────────────────────────────────────────────────

const toIsoDate = (date: string): string | null => {
  const t = date?.trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return t; // already ISO or unknown format — send as is
};

const isoToGerman = (iso: string | null): string => {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const computeDateRange = (e: EduEntry): string => {
  if (e.startYear) {
    const start = e.startMonth ? `${e.startMonth} ${e.startYear}` : e.startYear;
    const end = e.isCurrentJob ? "heute" : (e.endYear ? (e.endMonth ? `${e.endMonth} ${e.endYear}` : e.endYear) : "");
    return end ? `${start} – ${end}` : start;
  }
  return e.dateRange || "";
};

const WORK_MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const CUR_YEAR = new Date().getFullYear();
const WORK_YEARS = Array.from({ length: CUR_YEAR - 1989 }, (_, i) => String(CUR_YEAR - i));

const EMPLOYMENT_TYPES = ["Vollzeit", "Teilzeit", "Minijob / 450€-Job", "Werkstudent", "Aushilfe", "Freelance / Selbstständig", "Duales Studium", "Ausbildung", "Ehrenamtlich"];
const CAREER_LEVELS = ["Schüler/in", "Student/in", "Berufseinsteiger/in", "Junior", "Erfahren", "Senior", "Lead / Teamleiter/in", "Manager/in", "Sonstiges"];
const ACTIVITY_FIELDS = ["Administration / Büro", "Design / Kreativ / Medien", "Engineering / Technik", "Finanzen / Controlling", "Gesundheit / Soziales", "IT / Software", "Kommunikation / Marketing", "Kundenservice", "Logistik", "Recht / Compliance", "Vertrieb / Verkauf", "Bildung / Erziehung", "Sonstiges"];
const INDUSTRIES = ["Automobilindustrie", "Bau / Immobilien", "Beratung / Consulting", "Bildung / Wissenschaft", "Chemie / Pharma", "E-Commerce", "Energie / Umwelt", "Finanzen / Banken", "Gastgewerbe / Tourismus", "Gesundheit / Medizin", "IT / Software / Internet", "Industrie / Fertigung", "Logistik / Transport", "Medien / Werbung", "Öffentlicher Dienst / NGO", "Telekommunikation", "Sonstiges"];
const ORG_TYPES = ["Privatunternehmen", "Startup", "Mittelstand", "Konzern / Großunternehmen", "Öffentlicher Dienst / Behörde", "NGO / Non-Profit", "Forschungseinrichtung", "Bildungseinrichtung", "Sonstiges"];
const COMPANY_SIZES = ["1–10 Mitarbeiter", "11–50 Mitarbeiter", "51–200 Mitarbeiter", "201–500 Mitarbeiter", "501–1000 Mitarbeiter", "1001–5000 Mitarbeiter", "5000+ Mitarbeiter"];

// ─── Main Component ───────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const StudentCVBuilder: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [profile, setProfile] = useState<{ fullName: string; firstName: string; lastName: string; dateOfBirth: string | null; locked: boolean } | null>(null);

  const [state, setState] = useState<CVState>(() => {
    try {
      const s = localStorage.getItem(LS_KEY);
      if (s) return JSON.parse(s);
    } catch {}
    return DEFAULT_STATE;
  });

  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [mobileView, setMobileView] = useState<"form" | "preview">("form");

  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });

  // Auto-save to localStorage
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
  }, [state]);

  // Pre-fill from student account
  useEffect(() => {
    if (!state.personal.firstName && !state.personal.lastName && student) {
      setState(prev => ({
        ...prev,
        personal: {
          ...prev.personal,
          firstName: student.firstName || "",
          lastName: student.lastName || "",
          email: student.email || "",
          phone: (student as any).whatsappNumber?.toString() || "",
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  // Access check + profile load
  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/student/cv-builder/check-access`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API_BASE}/api/student/cv-builder/profile`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([accessData, profileData]) => {
      setHasAccess(!!accessData?.hasAccess);
      if (profileData?.locked) {
        setProfile(profileData);
        setState(prev => ({
          ...prev,
          personal: {
            ...prev.personal,
            firstName: profileData.firstName || "",
            lastName: profileData.lastName || "",
            birthDate: profileData.dateOfBirth ? isoToGerman(profileData.dateOfBirth) : prev.personal.birthDate,
          },
        }));
      }
    }).finally(() => setCheckingAccess(false));
  }, []);

  // Photo drag global listeners
  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setState(prev => ({
      ...prev,
      photo: { ...prev.photo, x: prev.photo.x + dx, y: prev.photo.y + dy },
    }));
  }, []);

  const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  // ─── State helpers ──────────────────────────────────────────────────────────

  const setP = (k: keyof PersonalInfo, v: any) =>
    setState(prev => ({ ...prev, personal: { ...prev.personal, [k]: v } }));

  const updateSec = (id: string, fn: (s: MainSection) => MainSection) =>
    setState(prev => ({ ...prev, sections: prev.sections.map(s => s.id === id ? fn(s) : s) }));

  const addSection = (type: MainSectionType) => {
    const def = ALL_SECTION_DEFS.find(d => d.type === type)!;
    const newSec: MainSection = { id: uuidv4(), type, label: def.label, entries: [] };
    setState(prev => ({ ...prev, sections: [...prev.sections, newSec] }));
    setExpandedSection(newSec.id);
    setShowAddPicker(false);
  };

  const removeSection = (id: string) =>
    setState(prev => ({ ...prev, sections: prev.sections.filter(s => s.id !== id) }));

  const moveSection = (id: string, dir: "up" | "down") => {
    setState(prev => {
      const arr = [...prev.sections];
      const i = arr.findIndex(s => s.id === id);
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...prev, sections: arr };
    });
  };

  // Edu helpers
  const addEdu = (sid: string) =>
    updateSec(sid, s => ({ ...s, entries: [...(s.entries as EduEntry[]), { id: uuidv4(), dateRange: "", title: "", tag: "", institution: "", fields: [] }] }));
  const updEdu = (sid: string, eid: string, k: keyof EduEntry, v: any) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as EduEntry[]).map(e => e.id === eid ? { ...e, [k]: v } : e) }));
  const delEdu = (sid: string, eid: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as EduEntry[]).filter(e => e.id !== eid) }));
  const addField = (sid: string, eid: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as EduEntry[]).map(e => e.id === eid ? { ...e, fields: [...e.fields, { id: uuidv4(), key: "", value: "" }] } : e) }));
  const updField = (sid: string, eid: string, fid: string, k: "key" | "value", v: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as EduEntry[]).map(e => e.id === eid ? { ...e, fields: e.fields.map(f => f.id === fid ? { ...f, [k]: v } : f) } : e) }));
  const delField = (sid: string, eid: string, fid: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as EduEntry[]).map(e => e.id === eid ? { ...e, fields: e.fields.filter(f => f.id !== fid) } : e) }));
  const toggleFieldGrade = (sid: string, eid: string, fid: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as EduEntry[]).map(e => e.id === eid ? { ...e, fields: e.fields.map(f => f.id === fid ? { ...f, isGrade: !f.isGrade } : f) } : e) }));

  // Lang helpers
  const addLang = (sid: string) =>
    updateSec(sid, s => ({ ...s, entries: [...(s.entries as LangEntry[]), { id: uuidv4(), name: "", level: "" }] }));
  const updLang = (sid: string, eid: string, k: "name" | "level", v: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as LangEntry[]).map(e => e.id === eid ? { ...e, [k]: v } : e) }));
  const delLang = (sid: string, eid: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as LangEntry[]).filter(e => e.id !== eid) }));

  // Simple helpers
  const addSimple = (sid: string) =>
    updateSec(sid, s => ({ ...s, entries: [...(s.entries as SimpleEntry[]), { id: uuidv4(), value: "" }] }));
  const updSimple = (sid: string, eid: string, v: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as SimpleEntry[]).map(e => e.id === eid ? { ...e, value: v } : e) }));
  const delSimple = (sid: string, eid: string) =>
    updateSec(sid, s => ({ ...s, entries: (s.entries as SimpleEntry[]).filter(e => e.id !== eid) }));

  // Hobby helpers
  const addHobby = () =>
    setState(prev => ({ ...prev, personal: { ...prev.personal, hobbies: [...prev.personal.hobbies, { id: uuidv4(), value: "" }] } }));
  const updHobby = (id: string, v: string) =>
    setState(prev => ({ ...prev, personal: { ...prev.personal, hobbies: prev.personal.hobbies.map(h => h.id === id ? { ...h, value: v } : h) } }));
  const delHobby = (id: string) =>
    setState(prev => ({ ...prev, personal: { ...prev.personal, hobbies: prev.personal.hobbies.filter(h => h.id !== id) } }));

  const handleReset = () => {
    if (window.confirm("Reset all CV data? This cannot be undone.")) {
      setState(DEFAULT_STATE);
      localStorage.removeItem(LS_KEY);
    }
  };

  const handleDownloadPDF = async () => {
    const fullName = `${state.personal.firstName} ${state.personal.lastName}`.trim();
    // Lock name + dob on first download if not yet saved
    if (!profile?.locked && state.personal.firstName) {
      try {
        const res = await fetch(`${API_BASE}/api/student/cv-builder/profile`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: state.personal.firstName,
            lastName: state.personal.lastName,
            dateOfBirth: toIsoDate(state.personal.birthDate),
          }),
        });
        if (res.ok) {
          const saved = await res.json();
          setProfile(saved);
        }
      } catch {}
    }
    try {
      // Awaited (not fire-and-forget) + keepalive: window.print() below can freeze/suspend
      // the page (e.g. native print/share sheet on mobile Safari), which was silently
      // killing this request before it reached the server — hence downloads never got
      // recorded in production. keepalive lets the request survive that suspension too.
      await fetch(`${API_BASE}/api/student/cv-builder/record-download`, {
        method: "POST", credentials: "include", keepalive: true,
      });
    } catch {}
    const originalTitle = document.title;
    const namePart = fullName.replace(/\s+/g, "_") || "Lebenslauf";
    document.title = `Lebenslauf_${namePart}`;
    window.print();
    setTimeout(() => { document.title = originalTitle; }, 2000);
  };

  const addableSections = ALL_SECTION_DEFS.filter(d => !state.sections.some(s => s.type === d.type));

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#4A729A]">
          <FaSpinner className="animate-spin text-2xl" />
          <span className="text-lg">Checking access...</span>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaLock />}
          title="CV Builder"
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/student/student-dashboard" },
            { label: "CV Builder" },
          ]}
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaLock className="text-red-600 text-3xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h2>
            <p className="text-gray-600 mb-6">
              You don't have access to the CV Builder. Please contact the administrator to request access.
            </p>
            <button
              onClick={() => navigate("/student/student-dashboard")}
              className="px-6 py-3 bg-gradient-to-r from-[#4A729A] to-[#2C4A6B] text-white rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 cv-builder-root">
      <style>{`
        @media screen {
          #cv-print-only { display: none !important; }
        }
        @media print {
          .no-print { display: none !important; }
          #cv-print-only { display: block !important; width: 210mm; }
          *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body { margin: 0; padding: 0; background: white; }
          @page { size: A4 portrait; margin: 0; }
        }
      `}</style>

      {/* Print-only CV — isolated from all screen layout classes */}
      <div id="cv-print-only">
        {state.format === "format3" ? <CVPreview3 state={state} />
          : state.format === "format2" ? <CVPreview2 state={state} />
          : <CVPreview state={state} />}
      </div>

      {/* Navigation Header — no-print: must not appear in the printed/PDF CV output */}
      <div className="no-print">
        <PageHeader
          icon={<FaIdCard />}
          title="CV Builder"
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/student/student-dashboard" },
            { label: "CV Builder" },
          ]}
        />
      </div>

      {/* Toolbar */}
      <div className="no-print bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <h1 className="text-sm font-bold text-gray-700">Format</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/student/cv-guide" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hidden sm:block">
            Anleitung
          </Link>
          <Link to="/student/grade-converter" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hidden sm:block">
            Notenrechner
          </Link>
          <select
            value={state.format ?? "classic"}
            onChange={e => setState(prev => ({ ...prev, format: e.target.value as "classic" | "format3" | "format2" }))}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:border-[#4A729A] bg-white"
          >
            <option value="classic">G2G Classic (Recommended)</option>
            <option value="format3">Modern Sidebar</option>
            <option value="format2">Executive</option>
          </select>
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"
          >
            <FaUndo size={11} /> Reset
          </button>
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-1.5 text-sm bg-[#4A729A] text-white rounded-lg hover:bg-[#3a5f84] flex items-center gap-1.5 font-semibold"
          >
            <FaDownload size={11} /> Download PDF
          </button>
        </div>
      </div>

      {/* Mobile tab bar */}
      <div className="no-print flex lg:hidden border-b border-gray-200 bg-white sticky top-[56px] z-10">
        <button
          onClick={() => setMobileView("form")}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mobileView === "form" ? "border-b-2 border-[#4A729A] text-[#4A729A]" : "text-gray-500"}`}
        >
          Edit
        </button>
        <button
          onClick={() => setMobileView("preview")}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mobileView === "preview" ? "border-b-2 border-[#4A729A] text-[#4A729A]" : "text-gray-500"}`}
        >
          Preview
        </button>
      </div>

      {/* Two-panel layout */}
      <div className="no-print flex lg:h-[calc(100vh-56px)]">

        {/* ── FORM PANEL ── */}
        <div className={`${mobileView === "form" ? "block" : "hidden"} lg:block w-full lg:w-[42%] overflow-y-auto bg-white border-r border-gray-200 p-4 space-y-4`}>

          {/* Color Theme — Format 2 */}
          {state.format === "format2" && (
            <FormBox title="Color Theme">
              <div className="flex flex-wrap gap-4">
                {CV2_THEMES.map(t => (
                  <button
                    key={t.id}
                    title={t.name}
                    onClick={() => setState(prev => ({ ...prev, themeId: t.id }))}
                    className="flex flex-col items-center gap-1 group"
                  >
                    <div
                      className={`w-9 h-9 rounded-full border-[3px] transition-all ${(state.themeId ?? "navy-gold") === t.id ? "border-gray-700 scale-110 shadow-md" : "border-gray-200 shadow-sm hover:scale-105"}`}
                      style={{ background: `linear-gradient(135deg, ${t.headerBg} 50%, ${t.accent} 50%)` }}
                    />
                    <span className="text-[9px] text-gray-400 group-hover:text-gray-600">{t.name}</span>
                  </button>
                ))}
              </div>
            </FormBox>
          )}

          {/* Color Theme — Format 3 only */}
          {(state.format ?? "classic") === "format3" && (
            <FormBox title="Color Theme">
              <div className="flex flex-wrap gap-4">
                {CV3_THEMES.map(t => (
                  <button
                    key={t.id}
                    title={t.name}
                    onClick={() => setState(prev => ({ ...prev, themeId: t.id }))}
                    className="flex flex-col items-center gap-1 group"
                  >
                    <div
                      className={`w-9 h-9 rounded-full border-[3px] transition-all ${(state.themeId ?? "navy-cyan") === t.id ? "border-gray-700 scale-110 shadow-md" : "border-gray-200 shadow-sm hover:scale-105"}`}
                      style={{ background: `linear-gradient(135deg, ${t.sideBg} 50%, ${t.accent} 50%)` }}
                    />
                    <span className="text-[9px] text-gray-400 group-hover:text-gray-600">{t.name}</span>
                  </button>
                ))}
              </div>
            </FormBox>
          )}

          {/* Photo */}
          <FormBox title="Photo">
            <div className="flex items-start gap-4">
              <div
                className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 border-2 border-gray-300 flex-shrink-0 relative"
                style={{ cursor: state.photo.base64 ? "move" : "default" }}
                onMouseDown={e => {
                  if (!state.photo.base64) return;
                  isDragging.current = true;
                  lastMouse.current = { x: e.clientX, y: e.clientY };
                  e.preventDefault();
                }}
              >
                {state.photo.base64 ? (
                  <img
                    src={state.photo.base64}
                    alt="CV"
                    draggable={false}
                    style={{
                      position: "absolute", width: "100%", height: "100%",
                      objectFit: "cover", pointerEvents: "none", userSelect: "none",
                      transform: `scale(${state.photo.zoom}) translate(${state.photo.x}px, ${state.photo.y}px)`,
                      transformOrigin: "center",
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <FaUser size={28} />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-gray-700">
                  Upload Photo
                  <input type="file" accept="image/*" className="hidden" onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setState(prev => ({ ...prev, photo: { base64: reader.result as string, x: 0, y: 0, zoom: 1 } }));
                    reader.readAsDataURL(file);
                  }} />
                </label>
                {state.photo.base64 && (
                  <>
                    <div className="flex items-center gap-2">
                      <FaSearchMinus className="text-gray-400" size={11} />
                      <input
                        type="range" min={0.5} max={3} step={0.05}
                        value={state.photo.zoom}
                        onChange={e => setState(prev => ({ ...prev, photo: { ...prev.photo, zoom: +e.target.value } }))}
                        className="flex-1 accent-[#4A729A]"
                      />
                      <FaSearchPlus className="text-gray-400" size={11} />
                    </div>
                    <p className="text-xs text-gray-400">Drag the circle to reposition</p>
                    <button
                      onClick={() => setState(prev => ({ ...prev, photo: { ...prev.photo, x: 0, y: 0, zoom: 1 } }))}
                      className="text-xs text-gray-400 hover:text-red-500"
                    >Reset position</button>
                  </>
                )}
              </div>
            </div>
          </FormBox>

          {/* Personal Info */}
          <FormBox title="Personal Information">
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <FL label={profile?.locked ? "Vorname * (gesperrt)" : "Vorname *"}>
                  <input
                    type="text"
                    value={state.personal.firstName}
                    onChange={e => !profile?.locked && setP("firstName", e.target.value)}
                    placeholder="Max"
                    readOnly={!!profile?.locked}
                    className={iCls + (profile?.locked ? " bg-gray-50 text-gray-500 cursor-not-allowed" : "")}
                  />
                </FL>
                <FL label={profile?.locked ? "Nachname * (gesperrt)" : "Nachname *"}>
                  <input
                    type="text"
                    value={state.personal.lastName}
                    onChange={e => !profile?.locked && setP("lastName", e.target.value)}
                    placeholder="Mustermann"
                    readOnly={!!profile?.locked}
                    className={iCls + (profile?.locked ? " bg-gray-50 text-gray-500 cursor-not-allowed" : "")}
                  />
                </FL>
              </div>
              <FL label="Subtitle (study field / job title)">
                <input type="text" value={state.personal.subtitle} onChange={e => setP("subtitle", e.target.value)} placeholder="Bachelor of Science – Informatik · TU Berlin" className={iCls} />
              </FL>
              <FL label="Address">
                <textarea
                  value={state.personal.address}
                  onChange={e => setP("address", e.target.value)}
                  placeholder={"Musterstraße 1\n10115 Berlin\nDeutschland"}
                  rows={3} className={iCls}
                />
              </FL>
              <div className="grid grid-cols-2 gap-2">
                <FL label="Phone">
                  <input type="text" value={state.personal.phone} onChange={e => setP("phone", e.target.value)} placeholder="+49 151 23456789" className={iCls} />
                </FL>
                <FL label="Email">
                  <input type="email" value={state.personal.email} onChange={e => setP("email", e.target.value)} placeholder="max@email.de" className={iCls} />
                </FL>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <FL label={profile?.locked ? "Birth Date (locked — contact admin)" : "Birth Date"}>
                  <input
                    type="text"
                    value={state.personal.birthDate}
                    onChange={e => !profile?.locked && setP("birthDate", e.target.value)}
                    placeholder="01.01.2000"
                    readOnly={!!profile?.locked}
                    className={iCls + (profile?.locked ? " bg-gray-50 text-gray-500 cursor-not-allowed" : "")}
                  />
                </FL>
                <FL label="Birth Place">
                  <input type="text" value={state.personal.birthPlace} onChange={e => setP("birthPlace", e.target.value)} placeholder="Berlin" className={iCls} />
                </FL>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <FL label="Nationality">
                  <input type="text" value={state.personal.nationality} onChange={e => setP("nationality", e.target.value)} placeholder="Deutsch" className={iCls} />
                </FL>
                <FL label="Marital Status">
                  <input type="text" value={state.personal.maritalStatus} onChange={e => setP("maritalStatus", e.target.value)} placeholder="Ledig" className={iCls} />
                </FL>
              </div>
            </div>
          </FormBox>

          {/* Hobbies */}
          <FormBox title="Hobbys & Interessen">
            <div className="space-y-1.5">
              {state.personal.hobbies.map(h => (
                <div key={h.id} className="flex gap-2">
                  <input type="text" value={h.value} onChange={e => updHobby(h.id, e.target.value)} placeholder="e.g. Fotografieren" className={iCls + " flex-1"} />
                  <button onClick={() => delHobby(h.id)} className="text-red-400 hover:text-red-600 px-1">
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
              <button onClick={addHobby} className={addBtnCls}>
                <FaPlus size={10} /> Add Hobby
              </button>
            </div>
          </FormBox>

          {/* Sections Manager */}
          <FormBox title="CV Sections">
            <div className="space-y-2">
              {state.sections.map((sec, idx) => {
                const isOpen = expandedSection === sec.id;
                const isEdu = ["education", "studium", "weiterbildung", "volunteering"].includes(sec.type);
                const isWork = ["experience", "praktikum"].includes(sec.type);
                const isLang = sec.type === "languages";
                const isSimple = ["skills", "activities"].includes(sec.type);
                return (
                  <div key={sec.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer select-none"
                      onClick={() => setExpandedSection(isOpen ? null : sec.id)}
                    >
                      <span className="flex-1 text-sm font-semibold text-gray-700">{sec.label}</span>
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => moveSection(sec.id, "up")} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-25">
                          <FaArrowUp size={11} />
                        </button>
                        <button onClick={() => moveSection(sec.id, "down")} disabled={idx === state.sections.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-25">
                          <FaArrowDown size={11} />
                        </button>
                        <button onClick={() => removeSection(sec.id)} className="p-1 text-red-400 hover:text-red-600">
                          <FaTrash size={11} />
                        </button>
                      </div>
                      {isOpen ? <FaChevronUp size={11} className="text-gray-400" /> : <FaChevronDown size={11} className="text-gray-400" />}
                    </div>

                    {isOpen && (
                      <div className="p-3 bg-white border-t border-gray-100 space-y-3">
                        {/* Edu-type editor */}
                        {isEdu && (
                          <>
                            {(sec.entries as EduEntry[]).map((entry, ei) => (
                              <div key={entry.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-bold text-gray-400">ENTRY {ei + 1}</span>
                                  <button onClick={() => delEdu(sec.id, entry.id)} className="text-red-400 hover:text-red-600"><FaTrash size={11} /></button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <FL label="Date Range">
                                    <input type="text" value={entry.dateRange} onChange={e => updEdu(sec.id, entry.id, "dateRange", e.target.value)} placeholder="2020 – 2024" className={iCls} />
                                  </FL>
                                  <FL label="Badge (optional)">
                                    <input type="text" value={entry.tag} onChange={e => updEdu(sec.id, entry.id, "tag", e.target.value)} placeholder="laufend" className={iCls} />
                                  </FL>
                                </div>
                                <FL label="Title">
                                  <input type="text" value={entry.title} onChange={e => updEdu(sec.id, entry.id, "title", e.target.value)} placeholder="Bachelor of Science – Informatik" className={iCls} />
                                </FL>
                                <FL label="Equivalent Class (optional)">
                                  <input type="text" value={entry.classLevel || ""} onChange={e => updEdu(sec.id, entry.id, "classLevel", e.target.value)} placeholder="10. Klasse" className={iCls} />
                                </FL>
                                <FL label="Institution">
                                  <input type="text" value={entry.institution} onChange={e => updEdu(sec.id, entry.id, "institution", e.target.value)} placeholder="Technische Universität Berlin" className={iCls} />
                                </FL>
                                {entry.fields.map(f => (
                                  <div key={f.id} className="flex gap-2 items-center">
                                    <input type="text" value={f.key} onChange={e => updField(sec.id, entry.id, f.id, "key", e.target.value)} placeholder="Fachrichtung" className={iClsNoW + " w-[30%] flex-shrink-0"} />
                                    <span className="text-gray-400 text-sm font-bold">:</span>
                                    <input type="text" value={f.value} onChange={e => updField(sec.id, entry.id, f.id, "value", e.target.value)} placeholder="Informatik" className={iCls + " flex-1"} />
                                    <label className="flex items-center gap-1 cursor-pointer flex-shrink-0" title="Show (Deutsches Notensystem)">
                                      <input type="checkbox" checked={!!f.isGrade} onChange={() => toggleFieldGrade(sec.id, entry.id, f.id)} className="accent-[#4A729A]" />
                                      <span className="text-[10px] text-gray-400 whitespace-nowrap">Note</span>
                                    </label>
                                    <button onClick={() => delField(sec.id, entry.id, f.id)} className="text-red-400 hover:text-red-600"><FaTrash size={10} /></button>
                                  </div>
                                ))}
                                <button onClick={() => addField(sec.id, entry.id)} className={addBtnCls}>
                                  <FaPlus size={9} /> Add Field
                                </button>
                              </div>
                            ))}
                            <button onClick={() => addEdu(sec.id)} className={addBtnCls}>
                              <FaPlus size={10} /> Add Entry
                            </button>
                          </>
                        )}

                        {/* Work-entry editor (experience / praktikum) */}
                        {isWork && (
                          <>
                            {(sec.entries as EduEntry[]).map((entry, ei) => (
                              <div key={entry.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-bold text-gray-400">EINTRAG {ei + 1}</span>
                                  <button onClick={() => delEdu(sec.id, entry.id)} className="text-red-400 hover:text-red-600"><FaTrash size={11} /></button>
                                </div>
                                <FL label="Positionsbezeichnung / Jobtitel">
                                  <input type="text" value={entry.title} onChange={e => updEdu(sec.id, entry.id, "title", e.target.value)} placeholder="z.B. Junior Software Developer" className={iCls} />
                                </FL>
                                <div className="grid grid-cols-2 gap-2">
                                  <FL label="Beschäftigungsart">
                                    <select value={entry.employmentType || ""} onChange={e => updEdu(sec.id, entry.id, "employmentType", e.target.value)} className={iCls}>
                                      <option value="">– Wählen –</option>
                                      {EMPLOYMENT_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                  </FL>
                                  <FL label="Karrierestufe">
                                    <select value={entry.careerLevel || ""} onChange={e => updEdu(sec.id, entry.id, "careerLevel", e.target.value)} className={iCls}>
                                      <option value="">– Wählen –</option>
                                      {CAREER_LEVELS.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                  </FL>
                                </div>
                                <FL label="Tätigkeitsfeld">
                                  <select value={entry.activityField || ""} onChange={e => updEdu(sec.id, entry.id, "activityField", e.target.value)} className={iCls}>
                                    <option value="">– Wählen –</option>
                                    {ACTIVITY_FIELDS.map(v => <option key={v} value={v}>{v}</option>)}
                                  </select>
                                </FL>
                                <FL label="Kurzbeschreibung / Aufgaben">
                                  <textarea value={entry.description || ""} onChange={e => updEdu(sec.id, entry.id, "description", e.target.value)} placeholder="Kurze Beschreibung der Tätigkeit..." rows={3} className={iCls} />
                                </FL>
                                <FL label="Unternehmen / Firma">
                                  <input type="text" value={entry.institution} onChange={e => updEdu(sec.id, entry.id, "institution", e.target.value)} placeholder="z.B. Siemens AG" className={iCls} />
                                </FL>
                                <div className="grid grid-cols-2 gap-2">
                                  <FL label="Branche">
                                    <select value={entry.industry || ""} onChange={e => updEdu(sec.id, entry.id, "industry", e.target.value)} className={iCls}>
                                      <option value="">– Wählen –</option>
                                      {INDUSTRIES.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                  </FL>
                                  <FL label="Unternehmensart">
                                    <select value={entry.orgType || ""} onChange={e => updEdu(sec.id, entry.id, "orgType", e.target.value)} className={iCls}>
                                      <option value="">– Wählen –</option>
                                      {ORG_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                  </FL>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <FL label="Unternehmensgröße">
                                    <select value={entry.companySize || ""} onChange={e => updEdu(sec.id, entry.id, "companySize", e.target.value)} className={iCls}>
                                      <option value="">– Wählen –</option>
                                      {COMPANY_SIZES.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                  </FL>
                                  <FL label="Standort (optional)">
                                    <input type="text" value={entry.location || ""} onChange={e => updEdu(sec.id, entry.id, "location", e.target.value)} placeholder="z.B. Berlin" className={iCls} />
                                  </FL>
                                </div>
                                <FL label="Website (optional)">
                                  <input type="text" value={entry.website || ""} onChange={e => updEdu(sec.id, entry.id, "website", e.target.value)} placeholder="z.B. www.unternehmen.de" className={iCls} />
                                </FL>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 mb-1">Zeitraum</label>
                                  <div className="grid grid-cols-2 gap-2 mb-2">
                                    <div className="flex gap-1">
                                      <select value={entry.startMonth || ""} onChange={e => updEdu(sec.id, entry.id, "startMonth", e.target.value)} className={iClsNoW + " flex-1"}>
                                        <option value="">Monat</option>
                                        {WORK_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                                      </select>
                                      <select value={entry.startYear || ""} onChange={e => updEdu(sec.id, entry.id, "startYear", e.target.value)} className={iClsNoW + " w-20"}>
                                        <option value="">Jahr</option>
                                        {WORK_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                      </select>
                                    </div>
                                    <div className="flex gap-1 items-center">
                                      {!entry.isCurrentJob && (
                                        <>
                                          <select value={entry.endMonth || ""} onChange={e => updEdu(sec.id, entry.id, "endMonth", e.target.value)} className={iClsNoW + " flex-1"}>
                                            <option value="">Monat</option>
                                            {WORK_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                                          </select>
                                          <select value={entry.endYear || ""} onChange={e => updEdu(sec.id, entry.id, "endYear", e.target.value)} className={iClsNoW + " w-20"}>
                                            <option value="">Jahr</option>
                                            {WORK_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                          </select>
                                        </>
                                      )}
                                      {entry.isCurrentJob && <span className="text-xs text-[#4A729A] font-semibold">Bis heute</span>}
                                    </div>
                                  </div>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={!!entry.isCurrentJob} onChange={() => updEdu(sec.id, entry.id, "isCurrentJob", !entry.isCurrentJob)} className="accent-[#4A729A]" />
                                    <span className="text-xs text-gray-600">Ich arbeite hier aktuell (Bis heute)</span>
                                  </label>
                                </div>
                                {entry.fields.map(f => (
                                  <div key={f.id} className="flex gap-2 items-center">
                                    <input type="text" value={f.key} onChange={e => updField(sec.id, entry.id, f.id, "key", e.target.value)} placeholder="Aufgabe" className={iClsNoW + " w-[30%] flex-shrink-0"} />
                                    <span className="text-gray-400 text-sm font-bold">:</span>
                                    <input type="text" value={f.value} onChange={e => updField(sec.id, entry.id, f.id, "value", e.target.value)} placeholder="Beschreibung" className={iCls + " flex-1"} />
                                    <button onClick={() => delField(sec.id, entry.id, f.id)} className="text-red-400 hover:text-red-600"><FaTrash size={10} /></button>
                                  </div>
                                ))}
                                <button onClick={() => addField(sec.id, entry.id)} className={addBtnCls}>
                                  <FaPlus size={9} /> Zusatzfeld
                                </button>
                              </div>
                            ))}
                            <button onClick={() => addEdu(sec.id)} className={addBtnCls}>
                              <FaPlus size={10} /> Eintrag hinzufügen
                            </button>
                          </>
                        )}

                        {/* Language editor */}
                        {isLang && (
                          <>
                            {(sec.entries as LangEntry[]).map(entry => (
                              <div key={entry.id} className="flex gap-2 items-center">
                                <input type="text" value={entry.name} onChange={e => updLang(sec.id, entry.id, "name", e.target.value)} placeholder="Deutsch" className={iCls + " flex-1"} />
                                <span className="text-gray-400 font-bold text-sm">:</span>
                                <input type="text" value={entry.level} onChange={e => updLang(sec.id, entry.id, "level", e.target.value)} placeholder="Muttersprache" className={iCls + " flex-1"} />
                                <button onClick={() => delLang(sec.id, entry.id)} className="text-red-400 hover:text-red-600"><FaTrash size={11} /></button>
                              </div>
                            ))}
                            <button onClick={() => addLang(sec.id)} className={addBtnCls}>
                              <FaPlus size={10} /> Add Language
                            </button>
                          </>
                        )}

                        {/* Skills / Activities editor */}
                        {isSimple && (
                          <>
                            {(sec.entries as SimpleEntry[]).map(entry => (
                              <div key={entry.id} className="flex gap-2 items-center">
                                <input type="text" value={entry.value} onChange={e => updSimple(sec.id, entry.id, e.target.value)} placeholder={sec.type === "skills" ? "Kommunikationsstärke" : "Teilnahme an Debatten"} className={iCls + " flex-1"} />
                                <button onClick={() => delSimple(sec.id, entry.id)} className="text-red-400 hover:text-red-600"><FaTrash size={11} /></button>
                              </div>
                            ))}
                            <button onClick={() => addSimple(sec.id)} className={addBtnCls}>
                              <FaPlus size={10} /> Add {sec.type === "skills" ? "Skill" : "Activity"}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add section picker */}
              {addableSections.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowAddPicker(p => !p)}
                    className="w-full text-sm border-2 border-dashed border-gray-300 rounded-lg py-2 text-gray-500 hover:border-[#4A729A] hover:text-[#4A729A] flex items-center justify-center gap-1.5"
                  >
                    <FaPlus size={10} /> Add Section
                  </button>
                  {showAddPicker && (
                    <div className="absolute left-0 right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                      {addableSections.map(def => (
                        <button
                          key={def.type}
                          onClick={() => addSection(def.type)}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700 border-b border-gray-100 last:border-0"
                        >
                          {def.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </FormBox>
        </div>

        {/* ── PREVIEW PANEL ── */}
        <div className={`cv-preview-panel ${mobileView === "preview" ? "block" : "hidden"} lg:flex flex-1 overflow-y-auto bg-gray-300 items-start justify-center p-4 lg:p-6`}>
          <div className="w-full overflow-x-auto">
          <div style={{ width: "850px", margin: "0 auto", minWidth: "850px" }}>
            {(() => {
              const dragStart = (e: React.MouseEvent) => {
                if (!state.photo.base64) return;
                isDragging.current = true;
                lastMouse.current = { x: e.clientX, y: e.clientY };
                e.preventDefault();
              };
              if (state.format === "format3") return <CVPreview3 state={state} onPhotoDragStart={dragStart} />;
              if (state.format === "format2") return <CVPreview2 state={state} onPhotoDragStart={dragStart} />;
              return <CVPreview state={state} onPhotoDragStart={dragStart} />;
            })()}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── CV Preview ───────────────────────────────────────────────────────────────

interface CVPreviewProps {
  state: CVState;
  onPhotoDragStart?: (e: React.MouseEvent) => void;
}

const CVPreview: React.FC<CVPreviewProps> = ({ state, onPhotoDragStart }) => {
  const { personal, photo, sections } = state;

  const renderSection = (sec: MainSection) => {
    const isEdu = ["education", "studium", "weiterbildung", "experience", "praktikum", "volunteering"].includes(sec.type);
    const isWork = ["experience", "praktikum"].includes(sec.type);
    const isLang = sec.type === "languages";
    const isSimple = ["skills", "activities"].includes(sec.type);

    if (isEdu) {
      const entries = sec.entries as EduEntry[];
      if (!entries.length) return null;
      return (
        <div key={sec.id} style={ps.mSection}>
          <div style={ps.mLabel}>{sec.label}</div>
          {entries.map((e, i) => (
            <div key={e.id} style={{
              ...ps.eduRow,
              borderBottom: i < entries.length - 1 ? "1px dashed #c8d8e4" : "none",
              paddingBottom: i < entries.length - 1 ? "12px" : 0,
              marginBottom: i < entries.length - 1 ? "12px" : 0,
            }}>
              <div style={ps.eduDate}>{isWork ? computeDateRange(e) : e.dateRange}</div>
              <div>
                <div style={ps.eduTitle}>
                  {e.title}
                  {e.classLevel && <span style={{ fontSize: "11px", fontWeight: 400, color: "#666" }}>({e.classLevel})</span>}
                  {e.tag && <span style={ps.tag}>{e.tag}</span>}
                  {isWork && e.employmentType && <span style={ps.tag}>{e.employmentType}</span>}
                </div>
                {e.institution && <div style={ps.eduInst}>{e.institution}{isWork && e.location ? ` · ${e.location}` : ""}</div>}
                {isWork && e.description && <div style={{ fontSize: "11px", color: "#444", marginTop: "3px", lineHeight: 1.5 }}>{e.description}</div>}
                {e.fields.length > 0 && (
                  <div style={ps.eduFields}>
                    {e.fields.map(f => (
                      <React.Fragment key={f.id}>
                        <span style={ps.fieldKey}>{f.key}</span>
                        <span style={ps.fieldVal}>
                          <span style={{ color: TC, fontWeight: 700 }}>:</span> {f.value}
                          {f.isGrade && <span style={{ color: "#888", fontStyle: "italic", fontSize: "10px" }}> (Deutsches Notensystem)</span>}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (isLang) {
      const entries = sec.entries as LangEntry[];
      if (!entries.length) return null;
      return (
        <div key={sec.id} style={ps.mSection}>
          <div style={ps.mLabel}>{sec.label}</div>
          {entries.map(e => (
            <div key={e.id} style={ps.langRow}>
              <span style={ps.langKey}>{e.name}</span>
              <span style={{ color: TC, fontWeight: 700 }}>:</span>
              <span style={ps.langVal}>{e.level}</span>
            </div>
          ))}
        </div>
      );
    }

    if (isSimple) {
      const entries = sec.entries as SimpleEntry[];
      if (!entries.length) return null;
      return (
        <div key={sec.id} style={ps.mSection}>
          <div style={ps.mLabel}>{sec.label}</div>
          <div style={ps.skillGrid}>
            {entries.map(e => (
              <div key={e.id} style={ps.skillItem}>
                <span style={{ color: TC, fontWeight: 700 }}>:</span> {e.value}
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const fullName = `${personal.firstName || ""} ${personal.lastName || ""}`.trim();
  const initials = fullName
    ? fullName.split(" ").map((w: string) => w[0]).join("").slice(0, 3).toUpperCase()
    : "?";

  const addressLines = personal.address ? personal.address.split("\n") : [];

  return (
    <div style={ps.cv}>
      {/* SIDEBAR */}
      <div style={ps.sidebar}>
        {/* Zone 1 – Photo */}
        <div style={ps.photoZone}>
          <div
            style={{ ...ps.photoWrap, cursor: photo.base64 ? "move" : "default" }}
            onMouseDown={onPhotoDragStart}
          >
            {photo.base64 ? (
              <img
                src={photo.base64}
                alt="Photo"
                draggable={false}
                style={{
                  position: "absolute", width: "100%", height: "100%",
                  objectFit: "cover", pointerEvents: "none", userSelect: "none",
                  transform: `scale(${photo.zoom}) translate(${photo.x}px, ${photo.y}px)`,
                  transformOrigin: "center",
                }}
              />
            ) : (
              <div style={{ fontSize: "32px", fontWeight: 700, color: "#fff", opacity: 0.7 }}>
                {initials}
              </div>
            )}
          </div>
        </div>

        {/* Zone 2 – Name */}
        <div style={ps.nameZone}>
          <div style={ps.sFieldLabel}>Vorname</div>
          <div style={{ ...ps.sFieldVal, fontWeight: 700, color: TC, marginBottom: "7px" }}>{personal.firstName || "–"}</div>
          <div style={ps.sFieldLabel}>Nachname</div>
          <div style={{ ...ps.sFieldVal, fontWeight: 700, color: TC }}>{personal.lastName || "–"}</div>
        </div>

        {/* Zone 3 – Info */}
        <div style={ps.infoZone}>
          {personal.address && (
            <div style={ps.sSection}>
              <div style={ps.sLabel}>Anschrift</div>
              <div style={ps.sText}>
                {addressLines.map((l, i) => (
                  <React.Fragment key={i}>{l}{i < addressLines.length - 1 && <br />}</React.Fragment>
                ))}
              </div>
            </div>
          )}
          {personal.phone && (
            <div style={ps.sSection}>
              <div style={ps.sLabel}>Telefon</div>
              <div style={ps.sText}>{personal.phone}</div>
            </div>
          )}
          {personal.email && (
            <div style={ps.sSection}>
              <div style={ps.sLabel}>E-Mail</div>
              <div style={ps.sText}>{personal.email}</div>
            </div>
          )}
          {(personal.birthDate || personal.birthPlace || personal.nationality || personal.maritalStatus) && (
            <div style={ps.sSection}>
              <div style={ps.sLabel}>Persönliches</div>
              {personal.birthDate && <><div style={ps.sFieldLabel}>Geburtsdatum</div><div style={ps.sFieldVal}>{personal.birthDate}</div></>}
              {personal.birthPlace && <><div style={{ ...ps.sFieldLabel, marginTop: "5px" }}>Geburtsort</div><div style={ps.sFieldVal}>{personal.birthPlace}</div></>}
              {personal.nationality && <><div style={{ ...ps.sFieldLabel, marginTop: "5px" }}>Staatsangehörigkeit</div><div style={ps.sFieldVal}>{personal.nationality}</div></>}
              {personal.maritalStatus && <><div style={{ ...ps.sFieldLabel, marginTop: "5px" }}>Familienstand</div><div style={ps.sFieldVal}>{personal.maritalStatus}</div></>}
            </div>
          )}
          {personal.hobbies.filter(h => h.value).length > 0 && (
            <div style={ps.sSection}>
              <div style={ps.sLabel}>Hobbys & Interessen</div>
              {personal.hobbies.filter(h => h.value).map(h => (
                <div key={h.id} style={ps.sText}>– {h.value}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MAIN */}
      <div style={ps.main}>
        <div style={ps.header}>
          <div style={{ fontSize: "26px", fontWeight: 700, color: "#fff", marginBottom: "4px" }}>
            {fullName || "Ihr Name"}
          </div>
          <div style={{ fontSize: "11.5px", color: "rgba(255,255,255,0.75)" }}>
            {personal.subtitle}
          </div>
        </div>
        <div style={ps.mainBody}>
          {sections.map(renderSection)}
        </div>
      </div>
    </div>
  );
};

// ─── Inline styles for CV preview ────────────────────────────────────────────

const ps = {
  cv: { display: "flex" as const, width: "100%", minHeight: "1100px", background: "#fff", fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif", color: "#2a2a2a", boxShadow: "0 4px 32px rgba(0,0,0,0.18)" },
  sidebar: { width: "30%", display: "flex" as const, flexDirection: "column" as const, flexShrink: 0 },
  photoZone: { background: "#C2D5E2", display: "flex" as const, flexDirection: "column" as const, alignItems: "center" as const, padding: "28px 16px 20px" },
  photoWrap: { width: "185px", height: "185px", borderRadius: "50%", overflow: "hidden" as const, border: "3px solid rgba(255,255,255,0.7)", background: "#8AAFC6", display: "flex" as const, alignItems: "center" as const, justifyContent: "center" as const, position: "relative" as const },
  nameZone: { background: "#A8C2D4", padding: "14px 18px" },
  sideName: { fontSize: "13px", fontWeight: 700 as const, color: TC, lineHeight: 1.4 },
  infoZone: { background: "#C2D5E2", flex: 1, padding: "18px 18px 32px" },
  sSection: { marginBottom: "14px" },
  sLabel: { fontSize: "8.5px", fontWeight: 700 as const, letterSpacing: "1.4px", textTransform: "uppercase" as const, color: TC, borderBottom: "1px solid rgba(44,74,107,0.35)", paddingBottom: "3px", marginBottom: "7px" },
  sText: { fontSize: "11px", lineHeight: 1.65, color: "#2a2a2a" },
  sFieldLabel: { fontSize: "8px", fontWeight: 700 as const, letterSpacing: "0.8px", textTransform: "uppercase" as const, color: TC, marginBottom: "1px" },
  sFieldVal: { fontSize: "11px", color: "#2a2a2a", lineHeight: 1.5 },
  main: { width: "70%", display: "flex" as const, flexDirection: "column" as const },
  header: { background: "#4A729A", padding: "22px 28px 18px" },
  mainBody: { padding: "20px 28px 32px", flex: 1 },
  mSection: { marginBottom: "20px" },
  mLabel: { fontSize: "9.5px", fontWeight: 700 as const, letterSpacing: "1.6px", textTransform: "uppercase" as const, color: TC, borderBottom: `1.5px solid ${TC}`, paddingBottom: "4px", marginBottom: "12px" },
  eduRow: { display: "grid" as const, gridTemplateColumns: "120px 1fr", gap: "0 14px" },
  eduDate: { fontSize: "11px", color: TC, fontWeight: 600 as const, lineHeight: 1.5, paddingTop: "1px" },
  eduTitle: { fontSize: "12.5px", fontWeight: 700 as const, color: "#2a2a2a", lineHeight: 1.4, display: "flex" as const, alignItems: "center" as const, gap: "8px", flexWrap: "wrap" as const, marginBottom: "2px" },
  tag: { display: "inline-block" as const, background: "#d0e4f0", color: TC, fontSize: "9px", fontWeight: 700 as const, letterSpacing: "0.5px", padding: "1px 7px", borderRadius: "3px" },
  eduInst: { fontSize: "11px", color: "#555", marginBottom: "5px" },
  eduFields: { display: "grid" as const, gridTemplateColumns: "auto 1fr", columnGap: "6px", rowGap: "1px" },
  fieldKey: { fontSize: "10.5px", fontWeight: 700 as const, color: "#2a2a2a", whiteSpace: "nowrap" as const },
  fieldVal: { fontSize: "10.5px", color: "#2a2a2a", lineHeight: 1.5 },
  langRow: { display: "grid" as const, gridTemplateColumns: "90px 8px 1fr", alignItems: "baseline" as const, gap: "0 4px", marginBottom: "4px" },
  langKey: { fontSize: "11.5px", fontWeight: 700 as const, color: "#2a2a2a" },
  langVal: { fontSize: "11.5px", color: "#2a2a2a" },
  skillGrid: { display: "grid" as const, gridTemplateColumns: "1fr 1fr", gap: "3px 12px" },
  skillItem: { display: "flex" as const, alignItems: "baseline" as const, gap: "5px", fontSize: "11px", color: "#2a2a2a", lineHeight: 1.6 },
};

// ─── CV Preview 2 (Executive) ────────────────────────────────────────────────

const langLevelToDots = (level: string): number => {
  const l = level.toLowerCase().trim();
  if (l === "muttersprache" || l === "native" || l === "c2") return 5;
  if (l === "c1" || l === "b2") return 4;
  if (l === "b1") return 3;
  if (l === "a2") return 2;
  return 1;
};

const makePS2 = (t: CV2Theme) => {
  const headerText = t.light ? "#1a1a1a" : "#fff";
  const headerSubtext = t.light ? "#555" : "#ccd6f0";
  const sectionColor = t.light ? "#1a1a1a" : t.headerBg;
  const skillTagBg = t.light ? t.accent : t.headerBg;
  return ({
  cv: { width: "100%", minHeight: "1100px", background: "#fff", fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif", boxShadow: "0 4px 32px rgba(0,0,0,0.18)" },
  header: { background: t.headerBg, display: "flex" as const, alignItems: "center" as const, padding: "28px 36px", gap: "26px" },
  headerLeft: { flex: 1 },
  headerName: { fontSize: "28px", fontWeight: 700 as const, color: headerText, letterSpacing: "0.5px", lineHeight: 1.1 },
  headerTitle: { fontSize: "12px", color: t.accent, fontWeight: 600 as const, letterSpacing: "2.5px", textTransform: "uppercase" as const, marginTop: "5px" },
  headerContacts: { display: "flex" as const, flexWrap: "wrap" as const, gap: "4px 18px", marginTop: "14px" },
  headerContact: { fontSize: "11px", color: headerSubtext, lineHeight: 1.5, display: "flex" as const, alignItems: "center" as const, gap: "5px" },
  headerPhoto: { width: "130px", height: "130px", borderRadius: "50%", border: `3px solid ${t.accent}`, background: `${t.headerBg}cc`, overflow: "hidden" as const, flexShrink: 0, display: "flex" as const, alignItems: "center" as const, justifyContent: "center" as const, position: "relative" as const, cursor: "move" },
  infoStrip: { background: "#f4f4f4", borderBottom: `2px solid ${t.accent}`, display: "flex" as const },
  infoItem: { flex: 1, padding: "9px 0", textAlign: "center" as const, borderRight: "1px solid #ddd" },
  infoItemLast: { flex: 1, padding: "9px 0", textAlign: "center" as const },
  infoLabel: { fontSize: "8.5px", fontWeight: 700 as const, textTransform: "uppercase" as const, letterSpacing: "1.5px", color: "#888" },
  infoValue: { fontSize: "11px", fontWeight: 600 as const, color: sectionColor, marginTop: "2px" },
  body: { display: "flex" as const },
  colLeft: { width: "34%", background: "#f9f9f9", padding: "26px 20px", borderRight: "1px solid #eee" },
  colRight: { flex: 1, padding: "26px 26px" },
  sectionTitle: { fontSize: "10.5px", fontWeight: 700 as const, textTransform: "uppercase" as const, letterSpacing: "2px", color: sectionColor, marginBottom: "12px", marginTop: "20px", paddingBottom: "4px", borderBottom: `1.5px solid ${t.accent}` },
  sectionTitleFirst: { fontSize: "10.5px", fontWeight: 700 as const, textTransform: "uppercase" as const, letterSpacing: "2px", color: sectionColor, marginBottom: "12px", marginTop: "0", paddingBottom: "4px", borderBottom: `1.5px solid ${t.accent}` },
  langRow: { display: "flex" as const, justifyContent: "space-between" as const, alignItems: "center" as const, marginBottom: "4px" },
  langName: { fontSize: "11px", color: "#2a2a2a" },
  langDots: { display: "flex" as const, gap: "3px" },
  langLevel: { fontSize: "9px", color: "#888", marginBottom: "6px", textAlign: "right" as const },
  skillTag: { display: "inline-block" as const, background: skillTagBg, color: "#fff", fontSize: "10px", padding: "3px 9px", borderRadius: "20px", margin: "2px 3px 2px 0" },
  skillTagAlt: { display: "inline-block" as const, background: t.accent, color: "#fff", fontSize: "10px", padding: "3px 9px", borderRadius: "20px", margin: "2px 3px 2px 0" },
  hobbies: { fontSize: "11px", color: "#444", lineHeight: 1.7 },
  entry: { marginBottom: "16px" },
  entryDate: { fontSize: "10px", fontWeight: 700 as const, color: t.accent, textTransform: "uppercase" as const, letterSpacing: "0.5px" },
  entryTitle: { fontSize: "12.5px", fontWeight: 700 as const, color: sectionColor, marginTop: "1px", display: "flex" as const, alignItems: "center" as const, gap: "6px", flexWrap: "wrap" as const },
  entryClassLevel: { fontSize: "10.5px", fontWeight: 400 as const, color: "#666" },
  entryTag: { display: "inline-block" as const, fontSize: "9px", fontWeight: 700 as const, color: t.accent, background: `${t.accent}18`, border: `1px solid ${t.accent}44`, padding: "1px 6px", borderRadius: "3px" },
  entrySub: { fontSize: "11px", color: "#666", marginTop: "1px", fontStyle: "italic" as const },
  entryFields: { marginTop: "4px" },
  entryField: { fontSize: "11px", color: "#444", padding: "1px 0", display: "flex" as const, gap: "6px" },
  entryFieldKey: { fontWeight: 600 as const, color: sectionColor, minWidth: "90px", flexShrink: 0 },
  entryGrade: { color: "#888", fontStyle: "italic" as const, fontSize: "10px" },
  });
};

const CVPreview2: React.FC<CVPreviewProps> = ({ state, onPhotoDragStart }) => {
  const theme = CV2_THEMES.find(t => t.id === (state.themeId ?? "navy-gold")) ?? CV2_THEMES[0];
  const s2 = makePS2(theme);
  const { personal, photo, sections } = state;

  const langSection = sections.find(s => s.type === "languages");
  const langs = (langSection?.entries as LangEntry[] ?? []);
  const skillSection = sections.find(s => s.type === "skills");
  const skills = (skillSection?.entries as SimpleEntry[] ?? []).filter(e => e.value);
  const actSection = sections.find(s => s.type === "activities");
  const activities = (actSection?.entries as SimpleEntry[] ?? []).filter(e => e.value);
  const rightSections = sections.filter(s => !["languages", "skills", "activities"].includes(s.type));

  const fullName = `${personal.firstName || ""} ${personal.lastName || ""}`.trim();
  const initials = fullName
    ? fullName.split(" ").map((w: string) => w[0]).join("").slice(0, 3).toUpperCase()
    : "?";
  const addressLines = personal.address ? personal.address.split("\n") : [];

  return (
    <div style={s2.cv}>
      {/* HEADER */}
      <div style={s2.header}>
        <div style={s2.headerLeft}>
          <div style={s2.headerName}>{fullName || "Ihr Name"}</div>
          {personal.subtitle && <div style={s2.headerTitle}>{personal.subtitle}</div>}
          <div style={s2.headerContacts}>
            {personal.email && (
              <span style={s2.headerContact}>
                <svg viewBox="0 0 20 20" style={{ width: 13, height: 13, fill: theme.accent, flexShrink: 0, display: "inline-block", verticalAlign: "middle", marginRight: 5 }}><path d="M2 4a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V4zm2 0v.01L10 11 16 4H4zm0 2.236V16h12V6.236l-6 5.4-6-5.4z"/></svg>
                {personal.email}
              </span>
            )}
            {personal.phone && (
              <span style={s2.headerContact}>
                <svg viewBox="0 0 20 20" style={{ width: 13, height: 13, fill: theme.accent, flexShrink: 0, display: "inline-block", verticalAlign: "middle", marginRight: 5 }}><path d="M2 3a1 1 0 011-1h3.5a1 1 0 011 1v3.5A1 1 0 016.5 7.5H5a9 9 0 009 9v-1.5a1 1 0 011-1H18a1 1 0 011 1V18a1 1 0 01-1 1A16 16 0 012 3z"/></svg>
                {personal.phone}
              </span>
            )}
            {addressLines[0] && (
              <span style={s2.headerContact}>
                <svg viewBox="0 0 20 20" style={{ width: 13, height: 13, fill: theme.accent, flexShrink: 0, display: "inline-block", verticalAlign: "middle", marginRight: 5 }}><path d="M10 2a6 6 0 00-6 6c0 4 6 10 6 10s6-6 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"/></svg>
                {addressLines.join(", ")}
              </span>
            )}
          </div>
        </div>
        <div style={s2.headerPhoto} onMouseDown={onPhotoDragStart}>
          {photo.base64 ? (
            <img src={photo.base64} alt="Photo" draggable={false} style={{ position: "absolute", width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none", userSelect: "none", transform: `scale(${photo.zoom}) translate(${photo.x}px, ${photo.y}px)`, transformOrigin: "center" }} />
          ) : (
            <div style={{ fontSize: "32px", fontWeight: 700, color: theme.accent }}>{initials}</div>
          )}
        </div>
      </div>

      {/* INFO STRIP */}
      {(personal.birthDate || personal.birthPlace || personal.nationality || personal.maritalStatus) && (
        <div style={s2.infoStrip}>
          {personal.birthDate && <div style={s2.infoItem}><div style={s2.infoLabel}>Geburtsdatum</div><div style={s2.infoValue}>{personal.birthDate}</div></div>}
          {personal.birthPlace && <div style={s2.infoItem}><div style={s2.infoLabel}>Geburtsort</div><div style={s2.infoValue}>{personal.birthPlace}</div></div>}
          {personal.nationality && <div style={s2.infoItem}><div style={s2.infoLabel}>Staatsangehörigkeit</div><div style={s2.infoValue}>{personal.nationality}</div></div>}
          {personal.maritalStatus && <div style={s2.infoItemLast}><div style={s2.infoLabel}>Familienstand</div><div style={s2.infoValue}>{personal.maritalStatus}</div></div>}
        </div>
      )}

      {/* BODY */}
      <div style={s2.body}>
        {/* LEFT */}
        <div style={s2.colLeft}>
          {(personal.firstName || personal.lastName) && (
            <>
              <div style={s2.sectionTitleFirst}>Name</div>
              {personal.firstName && (
                <div style={{ marginBottom: "6px" }}>
                  <div style={{ fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "1px", color: "#888", marginBottom: "1px" }}>Vorname</div>
                  <div style={{ fontSize: "11px", color: "#2a2a2a" }}>{personal.firstName}</div>
                </div>
              )}
              {personal.lastName && (
                <div style={{ marginBottom: "14px" }}>
                  <div style={{ fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "1px", color: "#888", marginBottom: "1px" }}>Nachname</div>
                  <div style={{ fontSize: "11px", color: "#2a2a2a" }}>{personal.lastName}</div>
                </div>
              )}
            </>
          )}
          {langs.length > 0 && (
            <>
              <div style={(personal.firstName || personal.lastName) ? s2.sectionTitle : s2.sectionTitleFirst}>Sprachkenntnisse</div>
              {langs.map(e => {
                const dots = langLevelToDots(e.level);
                return (
                  <div key={e.id}>
                    <div style={s2.langRow}>
                      <span style={s2.langName}>{e.name}</span>
                      <div style={s2.langDots}>
                        {[1,2,3,4,5].map(i => (
                          <div key={i} style={{ width: "8px", height: "8px", borderRadius: "50%", background: i <= dots ? theme.accent : "#e0e0e0" }} />
                        ))}
                      </div>
                    </div>
                    <div style={s2.langLevel}>{e.level}</div>
                  </div>
                );
              })}
            </>
          )}

          {skills.length > 0 && (
            <>
              <div style={langs.length ? s2.sectionTitle : s2.sectionTitleFirst}>Fähigkeiten</div>
              <div>{skills.map((sk, i) => <span key={sk.id} style={i % 2 === 0 ? s2.skillTag : s2.skillTagAlt}>{sk.value}</span>)}</div>
            </>
          )}

          {activities.length > 0 && (
            <>
              <div style={s2.sectionTitle}>Stärken & Aktivitäten</div>
              <div>{activities.map((a, i) => <span key={a.id} style={i % 2 === 0 ? s2.skillTag : s2.skillTagAlt}>{a.value}</span>)}</div>
            </>
          )}

          {personal.hobbies.filter(h => h.value).length > 0 && (
            <>
              <div style={s2.sectionTitle}>Hobbys</div>
              <div style={s2.hobbies}>{personal.hobbies.filter(h => h.value).map(h => h.value).join(" · ")}</div>
            </>
          )}
        </div>

        {/* RIGHT */}
        <div style={s2.colRight}>
          {rightSections.map((sec, si) => {
            const isWork = ["experience", "praktikum"].includes(sec.type);
            const entries = sec.entries as EduEntry[];
            if (!entries.length) return null;
            return (
              <div key={sec.id}>
                <div style={si === 0 ? s2.sectionTitleFirst : s2.sectionTitle}>{sec.label}</div>
                {entries.map(e => (
                  <div key={e.id} style={s2.entry}>
                    <div style={s2.entryDate}>{isWork ? computeDateRange(e) : e.dateRange}</div>
                    <div style={s2.entryTitle}>
                      {e.title}
                      {e.classLevel && <span style={s2.entryClassLevel}> ({e.classLevel})</span>}
                      {e.tag && <span style={s2.entryTag}>{e.tag}</span>}
                      {isWork && e.employmentType && <span style={s2.entryTag}>{e.employmentType}</span>}
                    </div>
                    {e.institution && <div style={s2.entrySub}>{e.institution}{isWork && e.location ? ` · ${e.location}` : ""}</div>}
                    {isWork && e.description && <div style={{ fontSize: "11px", color: "#444", marginTop: "3px", lineHeight: 1.5 }}>{e.description}</div>}
                    {e.fields.length > 0 && (
                      <div style={s2.entryFields}>
                        {e.fields.map(f => (
                          <div key={f.id} style={s2.entryField}>
                            <span style={s2.entryFieldKey}>{f.key}:</span>
                            <span>{f.value}{f.isGrade && <span style={s2.entryGrade}> (Deutsches Notensystem)</span>}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── CV Preview 3 (Modern Sidebar) ──────────────────────────────────────────

const langLevelToPercent = (level: string): number => {
  const l = level.toLowerCase().trim();
  if (l === "muttersprache" || l === "native") return 100;
  if (l === "c2") return 95;
  if (l === "c1") return 85;
  if (l === "b2") return 70;
  if (l === "b1") return 55;
  if (l === "a2") return 35;
  if (l === "a1") return 20;
  return 60;
};

const makePS3 = (t: CV3Theme) => {
  const sideText = t.light ? "rgba(0,0,0,0.78)" : "rgba(255,255,255,0.85)";
  const sideLabel = t.light ? "rgba(0,0,0,0.40)" : "rgba(255,255,255,0.40)";
  const sideTagText = t.light ? t.accent : "rgba(255,255,255,0.82)";
  const rightHeading = t.light ? "#1A1A1A" : t.sideBg;
  const barStart = t.light ? `${t.accent}55` : t.sideBg;
  return ({
  cv: { display: "flex" as const, width: "100%", minHeight: "1100px", background: "#fff", fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif", boxShadow: "0 4px 32px rgba(0,0,0,0.18)" },
  leftStrip: { width: "34%", background: t.sideBg, padding: "30px 18px", display: "flex" as const, flexDirection: "column" as const, alignItems: "center" as const, flexShrink: 0 },
  photoWrap: { width: "145px", height: "145px", borderRadius: "50%", overflow: "hidden" as const, border: `3px solid ${t.accent}`, background: t.sideBg, display: "flex" as const, alignItems: "center" as const, justifyContent: "center" as const, position: "relative" as const, marginBottom: "24px", alignSelf: "center" as const, flexShrink: 0, cursor: "move" },
  stripSection: { marginBottom: "18px", width: "100%" },
  stripHeading: { fontSize: "8px", fontWeight: 700 as const, textTransform: "uppercase" as const, letterSpacing: "2.5px", color: t.accent, marginBottom: "8px", paddingBottom: "4px", borderBottom: `1px solid ${t.accent}44` },
  stripItem: { marginBottom: "7px" },
  stripLabel: { fontSize: "7.5px", fontWeight: 600 as const, textTransform: "uppercase" as const, letterSpacing: "0.7px", color: sideLabel, marginBottom: "1px" },
  stripValue: { fontSize: "10px", color: sideText, lineHeight: 1.55 },
  stripTags: { display: "flex" as const, flexWrap: "wrap" as const, gap: "4px" },
  stripTag: { background: `${t.accent}1a`, border: `1px solid ${t.accent}44`, color: sideTagText, fontSize: "8.5px", padding: "2px 6px", borderRadius: "3px", lineHeight: 1.4 },
  rightContent: { flex: 1, padding: "30px 26px", minWidth: 0 },
  nameBlock: { marginBottom: "18px" },
  cvName: { fontSize: "24px", fontWeight: 700 as const, color: rightHeading, lineHeight: 1.1 },
  cvSubtitle: { fontSize: "11px", color: "#666", marginTop: "4px", fontStyle: "italic" as const },
  nameDivider: { width: "36px", height: "3px", background: t.accent, marginTop: "9px", borderRadius: "2px" },
  section: { marginBottom: "16px" },
  sectionHeader: { display: "flex" as const, alignItems: "center" as const, gap: "8px", marginBottom: "10px" },
  sectionBar: { width: "4px", height: "15px", background: t.accent, borderRadius: "2px", flexShrink: 0 },
  sectionTitle: { fontSize: "9.5px", fontWeight: 700 as const, textTransform: "uppercase" as const, letterSpacing: "2px", color: rightHeading, whiteSpace: "nowrap" as const },
  sectionLine: { flex: 1, height: "1px", background: "#e8e8e8" },
  entry: { display: "flex" as const, alignItems: "flex-start" as const, gap: "10px", marginBottom: "12px" },
  entryDate: { flexShrink: 0, minWidth: "100px", textAlign: "center" as const, fontSize: "9px", fontWeight: 700 as const, color: t.light ? t.accent : "#fff", background: t.light ? `${t.accent}18` : t.sideBg, border: t.light ? `1px solid ${t.accent}55` : "none", padding: "2px 7px", borderRadius: "3px", whiteSpace: "nowrap" as const, lineHeight: 1.5, marginTop: "1px" },
  entryContent: { flex: 1, minWidth: 0 },
  entryTitle: { fontSize: "12px", fontWeight: 700 as const, color: "#1A1A1A", lineHeight: 1.3, marginBottom: "1px", display: "flex" as const, alignItems: "center" as const, gap: "6px", flexWrap: "wrap" as const },
  entryClassLevel: { fontSize: "10.5px", fontWeight: 400 as const, color: "#666" },
  entryTag: { display: "inline-block" as const, fontSize: "9px", fontWeight: 700 as const, color: t.accent, background: `${t.accent}18`, border: `1px solid ${t.accent}44`, padding: "1px 6px", borderRadius: "3px" },
  entrySub: { fontSize: "10px", color: "#888", fontStyle: "italic" as const, marginBottom: "3px" },
  entryField: { fontSize: "10.5px", color: "#444", lineHeight: 1.55 },
  entryKey: { fontWeight: 700 as const, color: rightHeading },
  entryGrade: { color: "#888", fontStyle: "italic" as const, fontSize: "10px" },
  langGrid: { display: "grid" as const, gridTemplateColumns: "1fr 1fr", gap: "8px 22px" },
  langItemTop: { display: "flex" as const, justifyContent: "space-between" as const, alignItems: "baseline" as const, marginBottom: "3px" },
  langName: { fontSize: "11px", fontWeight: 600 as const, color: rightHeading },
  langLevel: { fontSize: "9px", fontWeight: 700 as const, color: t.accent, background: `${t.accent}14`, border: `1px solid ${t.accent}30`, padding: "1px 6px", borderRadius: "10px" },
  langBarBg: { height: "4px", background: "#ebebeb", borderRadius: "2px", overflow: "hidden" as const },
  langBarFill: { height: "100%", background: `linear-gradient(90deg, ${barStart}, ${t.accent})`, borderRadius: "2px" },
  });
};

const CVPreview3: React.FC<CVPreviewProps> = ({ state, onPhotoDragStart }) => {
  const theme = CV3_THEMES.find(t => t.id === (state.themeId ?? "navy-cyan")) ?? CV3_THEMES[0];
  const s3 = makePS3(theme);
  const { personal, photo, sections } = state;

  const skillsSection = sections.find(sec => sec.type === "skills");
  const skills = (skillsSection?.entries as SimpleEntry[] ?? []).filter(e => e.value);
  const rightSections = sections.filter(sec => sec.type !== "skills");

  const fullName = `${personal.firstName || ""} ${personal.lastName || ""}`.trim();
  const initials = fullName
    ? fullName.split(" ").map((w: string) => w[0]).join("").slice(0, 3).toUpperCase()
    : "?";
  const addressLines = personal.address ? personal.address.split("\n") : [];

  const renderSection = (sec: MainSection) => {
    if (sec.type === "languages") {
      const entries = sec.entries as LangEntry[];
      if (!entries.length) return null;
      return (
        <div key={sec.id} style={s3.section}>
          <div style={s3.sectionHeader}>
            <div style={s3.sectionBar} /><div style={s3.sectionTitle}>{sec.label}</div><div style={s3.sectionLine} />
          </div>
          <div style={s3.langGrid}>
            {entries.map(e => (
              <div key={e.id}>
                <div style={s3.langItemTop}>
                  <span style={s3.langName}>{e.name}</span>
                  <span style={s3.langLevel}>{e.level}</span>
                </div>
                <div style={s3.langBarBg}><div style={{ ...s3.langBarFill, width: `${langLevelToPercent(e.level)}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    const isWork = ["experience", "praktikum"].includes(sec.type);
    const entries = sec.entries as EduEntry[];
    if (!entries.length) return null;
    return (
      <div key={sec.id} style={s3.section}>
        <div style={s3.sectionHeader}>
          <div style={s3.sectionBar} /><div style={s3.sectionTitle}>{sec.label}</div><div style={s3.sectionLine} />
        </div>
        {entries.map(e => (
          <div key={e.id} style={s3.entry}>
            <span style={s3.entryDate}>{isWork ? computeDateRange(e) : e.dateRange}</span>
            <div style={s3.entryContent}>
              <div style={s3.entryTitle}>
                {e.title}
                {e.classLevel && <span style={s3.entryClassLevel}> ({e.classLevel})</span>}
                {e.tag && <span style={s3.entryTag}>{e.tag}</span>}
                {isWork && e.employmentType && <span style={s3.entryTag}>{e.employmentType}</span>}
              </div>
              {e.institution && <div style={s3.entrySub}>{e.institution}{isWork && e.location ? ` · ${e.location}` : ""}</div>}
              {isWork && e.description && <div style={{ fontSize: "10.5px", color: "#444", marginTop: "3px", lineHeight: 1.5 }}>{e.description}</div>}
              {e.fields.map(f => (
                <div key={f.id} style={s3.entryField}>
                  <span style={s3.entryKey}>{f.key}:</span> {f.value}
                  {f.isGrade && <span style={s3.entryGrade}> (Deutsches Notensystem)</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={s3.cv}>
      <div style={s3.leftStrip}>
        <div style={s3.photoWrap} onMouseDown={onPhotoDragStart}>
          {photo.base64 ? (
            <img src={photo.base64} alt="Photo" draggable={false} style={{ position: "absolute", width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none", userSelect: "none", transform: `scale(${photo.zoom}) translate(${photo.x}px, ${photo.y}px)`, transformOrigin: "center" }} />
          ) : (
            <div style={{ fontSize: "30px", fontWeight: 700, color: theme.accent, opacity: 0.8 }}>{initials}</div>
          )}
        </div>

        {(personal.firstName || personal.lastName) && (
          <div style={s3.stripSection}>
            <div style={s3.stripHeading}>Name</div>
            {personal.firstName && <div style={s3.stripItem}><div style={s3.stripLabel}>Vorname</div><div style={s3.stripValue}>{personal.firstName}</div></div>}
            {personal.lastName && <div style={s3.stripItem}><div style={s3.stripLabel}>Nachname</div><div style={s3.stripValue}>{personal.lastName}</div></div>}
          </div>
        )}

        {(personal.email || personal.phone || personal.address) && (
          <div style={s3.stripSection}>
            <div style={s3.stripHeading}>Kontakt</div>
            {personal.email && <div style={s3.stripItem}><div style={s3.stripLabel}>E-Mail</div><div style={s3.stripValue}>{personal.email}</div></div>}
            {personal.phone && <div style={s3.stripItem}><div style={s3.stripLabel}>Telefon</div><div style={s3.stripValue}>{personal.phone}</div></div>}
            {personal.address && (
              <div style={s3.stripItem}>
                <div style={s3.stripLabel}>Adresse</div>
                <div style={s3.stripValue}>{addressLines.map((l, i) => <React.Fragment key={i}>{l}{i < addressLines.length - 1 && <br />}</React.Fragment>)}</div>
              </div>
            )}
          </div>
        )}

        <div style={s3.stripSection}>
          <div style={s3.stripHeading}>Persönlich</div>
          {personal.birthDate && <div style={s3.stripItem}><div style={s3.stripLabel}>Geburtsdatum</div><div style={s3.stripValue}>{personal.birthDate}</div></div>}
          {personal.birthPlace && <div style={s3.stripItem}><div style={s3.stripLabel}>Geburtsort</div><div style={s3.stripValue}>{personal.birthPlace}</div></div>}
          {personal.nationality && <div style={s3.stripItem}><div style={s3.stripLabel}>Staatsangehörigkeit</div><div style={s3.stripValue}>{personal.nationality}</div></div>}
          {personal.maritalStatus && <div style={s3.stripItem}><div style={s3.stripLabel}>Familienstand</div><div style={s3.stripValue}>{personal.maritalStatus}</div></div>}
        </div>

        {skills.length > 0 && (
          <div style={s3.stripSection}>
            <div style={s3.stripHeading}>Fähigkeiten</div>
            <div style={s3.stripTags}>{skills.map(sk => <span key={sk.id} style={s3.stripTag}>{sk.value}</span>)}</div>
          </div>
        )}

        {personal.hobbies.filter(h => h.value).length > 0 && (
          <div style={s3.stripSection}>
            <div style={s3.stripHeading}>Hobbys</div>
            <div style={{ ...s3.stripValue, lineHeight: 1.9 }}>{personal.hobbies.filter(h => h.value).map(h => h.value).join(" · ")}</div>
          </div>
        )}
      </div>

      <div style={s3.rightContent}>
        <div style={s3.nameBlock}>
          <div style={s3.cvName}>{fullName || "Ihr Name"}</div>
          {personal.subtitle && <div style={s3.cvSubtitle}>{personal.subtitle}</div>}
          <div style={s3.nameDivider} />
        </div>
        {rightSections.map(renderSection)}
      </div>
    </div>
  );
};

export default StudentCVBuilder;
